import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CreditCard, FilePdf, CheckCircle, WarningCircle, Receipt } from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";

/**
 * Public invoice payment landing page. Reached via the unguessable invoice
 * UUID embedded in the "Pay Now" link emailed to the recipient. NO login
 * required — the invoice ID itself is the secret.
 */
export default function PublicPayInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const justReturned = params.get("paid") === "1";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND}/api/public/invoices/${id}`);
        if (!cancelled) setData(resp.data);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "Could not load this invoice.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const startCheckout = async () => {
    setPaying(true);
    try {
      const resp = await axios.post(`${BACKEND}/api/public/invoices/${id}/wipay/checkout`);
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        toast.error("No payment URL returned");
        setPaying(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment initialisation failed");
      setPaying(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${BACKEND}/api/public/invoices/${id}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="text-sm text-zinc-500">Loading invoice…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] p-6" data-testid="pay-invoice-error">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-8 text-center">
          <WarningCircle size={32} className="mx-auto text-red-500 mb-3" weight="bold" />
          <h1 className="text-lg font-semibold mb-2" style={{ fontFamily: "Work Sans" }}>Invoice not found</h1>
          <p className="text-sm text-zinc-500">{error || "Please check the link in your email and try again."}</p>
        </div>
      </div>
    );
  }

  const { invoice, company } = data || {};
  if (!invoice || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="public-pay-page">
        <div className="max-w-md text-center">
          <WarningCircle size={32} className="mx-auto text-amber-500 mb-3" weight="bold" />
          <h1 className="text-lg font-semibold mb-2" style={{ fontFamily: "Work Sans" }}>Invoice unavailable</h1>
          <p className="text-sm text-zinc-500">This payment link looks malformed. Please ask the courier to re-send your invoice.</p>
        </div>
      </div>
    );
  }
  const isPaid = invoice.status === "paid";
  const cur = (invoice.currency || "JMD").toUpperCase();
  const amount = Number(invoice.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // WiPay Jamaica's hosted page rejects payments below 150 JMD. Hide the
  // online-pay CTA and surface a clear bank-transfer prompt instead.
  const belowWipayMinimum = cur === "JMD" && Number(invoice.amount || 0) < 150;
  const logoSrc = company?.public_slug
    ? `${BACKEND}/api/public/courier/${company.public_slug}/logo`
    : "/brand/kanal-logo.svg";

  return (
    <div className="min-h-screen bg-[#F4F4F5] py-10 px-4" data-testid="public-pay-page">
      <Toaster position="top-right" />
      <div className="max-w-lg mx-auto">
        {/* Courier branding */}
        <div className="flex items-center gap-3 mb-6">
          <img src={logoSrc} alt={company.name} className="h-12 w-12 rounded-md object-contain bg-white border border-zinc-200 p-1" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Invoice payment</div>
            <div className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }} data-testid="pay-company-name">
              {company.name}
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden shadow-sm">
          {/* Amount block */}
          <div className={`px-6 py-7 border-b border-zinc-200 ${isPaid ? "bg-emerald-50" : justReturned ? "bg-amber-50" : "bg-white"}`}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1">
              {isPaid ? "Already paid" : "Amount due"}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-mono text-zinc-500">{cur}</span>
              <span className="text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Work Sans" }} data-testid="pay-amount">
                ${amount}
              </span>
            </div>
            <div className="mt-3 text-xs text-zinc-500">
              Invoice <code className="font-mono bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5">{invoice.invoice_number}</code>
            </div>
          </div>

          {/* Status banner */}
          {isPaid && (
            <div className="px-6 py-4 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2 text-emerald-800 text-sm font-semibold" data-testid="pay-already-paid">
              <CheckCircle size={16} weight="fill" /> This invoice has been settled. Thank you!
            </div>
          )}
          {!isPaid && justReturned && (
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm" data-testid="pay-pending-banner">
              We're still waiting for WiPay to confirm your payment. This page will update once the confirmation arrives — usually within a minute.
            </div>
          )}

          {/* Bill-to row */}
          <div className="px-6 py-5 border-b border-zinc-200 space-y-1.5 text-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Bill to</div>
            <div className="font-semibold text-zinc-900" data-testid="pay-customer-name">{invoice.customer_name}</div>
            {invoice.customer_email && <div className="text-zinc-500">{invoice.customer_email}</div>}
            {invoice.due_date && <div className="text-[12px] text-zinc-500">Due {new Date(invoice.due_date).toLocaleDateString()}</div>}
          </div>

          {/* Itemised */}
          {invoice.base_amount != null && invoice.signature_surcharge ? (
            <div className="px-6 py-4 border-b border-zinc-200 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-zinc-600">Delivery</span><span>{cur} ${Number(invoice.base_amount).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-600">Signature surcharge</span><span>{cur} ${Number(invoice.signature_surcharge).toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 mt-1 border-t border-zinc-200 font-semibold text-zinc-900"><span>Total</span><span>{cur} ${amount}</span></div>
            </div>
          ) : null}

          {/* Action row */}
          <div className="px-6 py-5 space-y-3">
            {!isPaid && belowWipayMinimum && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="public-pay-min-banner">
                <div className="font-semibold mb-0.5">Bank transfer only</div>
                <div className="text-xs leading-relaxed">
                  WiPay does not accept online payments below <b>$150 JMD</b>.
                  Please use the bank-transfer details below to settle this invoice.
                </div>
              </div>
            )}
            {!isPaid && !belowWipayMinimum && (
              <Button
                onClick={startCheckout}
                disabled={paying}
                className="w-full h-12 bg-[#002BF6] hover:bg-[#0021C7] text-white text-base font-semibold"
                data-testid="public-pay-now-btn"
              >
                {paying ? "Redirecting to WiPay…" : (<><CreditCard size={18} weight="bold" className="mr-2" /> Pay {cur} ${amount} with WiPay</>)}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={downloadPdf}
              className="w-full"
              data-testid="public-pay-pdf-btn"
            >
              <FilePdf size={16} className="mr-2" /> Download invoice PDF
            </Button>
            {!isPaid && (
              <a
                href="https://jm.wipayfinancial.com/to_me/cyrusfieldtech"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  try {
                    navigator.clipboard?.writeText(invoice?.invoice_number || "");
                  } catch { /* clipboard not available */ }
                }}
                data-testid="public-pay-cyrusfield-link"
                className="inline-flex items-center justify-center w-full h-11 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors"
                title="Pay through Cyrus Field's WiPay account — reference your invoice number in the note field"
              >
                <CreditCard size={15} className="mr-2" weight="bold" />
                Pay via Cyrus Field WiPay (alt.)
              </a>
            )}
            {!isPaid && (
              <p className="text-[11px] text-zinc-500 leading-snug -mt-1">
                Cyrus Field's WiPay page accepts payments on our behalf. Reference your invoice # <span className="font-mono">{invoice?.invoice_number}</span> in the WiPay note so we can reconcile your payment.
              </p>
            )}
          </div>

          {/* Bank transfer fallback */}
          {!isPaid && (
            <details className="px-6 py-4 border-t border-zinc-200 text-sm" open={belowWipayMinimum} data-testid="public-pay-bank-block">
              <summary className="cursor-pointer text-zinc-600 font-medium flex items-center gap-1.5">
                <Receipt size={14} weight="bold" /> Prefer to pay by bank transfer?
              </summary>
              <div className="mt-3 text-xs text-zinc-600 space-y-2 pl-1">
                <p>Wire to Sagicor Bank (Tropical Plaza, Jamaica):</p>
                <div className="font-mono text-[12px] bg-zinc-50 border border-zinc-200 rounded p-3 space-y-2">
                  <div><span className="text-zinc-500">Beneficiary:</span> <b>Cyrus Field Media Capital Ltd</b></div>
                  <div className="pt-1.5 border-t border-zinc-200">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">USD Account</div>
                    <div>Account #: <b>5503986665</b> · Savings</div>
                  </div>
                  <div className="pt-1.5 border-t border-zinc-200">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">JMD Account</div>
                    <div>Account #: <b>5503875828</b> · Savings</div>
                  </div>
                  <div className="pt-1.5 border-t border-zinc-200">
                    Reference: <b>{invoice.invoice_number}</b>
                  </div>
                </div>
                <p>Reply to your invoice email with the wire confirmation # — we'll mark it cleared manually. Use the JMD account for Jamaican-dollar invoices and the USD account for US-dollar invoices.</p>
              </div>
            </details>
          )}
        </div>

        <div className="text-center mt-6 text-[11px] text-zinc-400">
          Powered by <b className="text-zinc-500">Kanal</b> · Secure payments via WiPay Jamaica
        </div>
      </div>
    </div>
  );
}
